import {
  useGetAgentsByFolderId,
  useGetAllKB,
  useGetFolderList,
} from '@/modules/gpt-chats/hooks/use-kb';
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { KB, KBType } from '@/modules/gpt-chats/models/kb';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useQueryState, useQueryStates, parseAsInteger } from 'nuqs';
import { BREADCRUMB_CUSTOM_TITLES } from '@/constant/breadcrumb-custom-title';
import { KB_TABS } from '@/modules/gpt-chats/utils/agent-kb.utils';
import { useTranslation } from 'react-i18next';
import { FolderConfigureModal } from './components/configure-folder-modal';
import { KBDocumentsTable } from './components/kb-documents-table';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { Settings } from 'lucide-react';
import { IFolder } from '@/modules/gpt-chats/types/kb.service.type';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui-kit/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';

import { KBDocumentPage } from '../kb-selected-document/kb-document-page';
import { Sparkles as CircleSpark } from 'lucide-react';

export const KBFolderPage = ({ folderId: propFolderId }: { folderId?: string }) => {
  const { t } = useTranslation();
  const params = useParams();
  const folderId = (propFolderId || params?.folder) as string;

  const { data: folderlistData } = useGetFolderList();
  const folderlist = folderlistData?.data?.folders;

  const folder = folderlist?.find((folder) => folder._id === folderId);

  BREADCRUMB_CUSTOM_TITLES['/ai/knowledge-base'] = t('KNOWLEDGE_BASE');
  BREADCRUMB_CUSTOM_TITLES[`/ai/knowledge-base/${folderId}`] = folder?.Name ?? t('FOLDER_LABEL');

  const [activeTab, setActiveTab] = useQueryState('type', { defaultValue: KBType.FILE });
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedKB, setSelectedKB] = useState<KB | null>(null);

  const [filter, setFilter] = useQueryStates({
    limit: parseAsInteger.withDefault(10),
    offset: parseAsInteger.withDefault(0),
  });

  const { data, isLoading } = useGetAllKB(
    {
      kb_type: activeTab as KBType,
      limit: filter.limit,
      offset: filter.offset,
      folder_id: folderId,
    },
    !!folderId
  );

  const { data: associatedAgentCount } = useGetAgentsByFolderId(
    { folder_id: folderId },
    !!folderId
  );

  const knowledgeBases = data?.data?.knowledge_bases || [];
  const totalCount = data?.data?.total_count || 0;

  const displayedKB = selectedKB
    ? knowledgeBases.find((kb) => kb.id === selectedKB.id) || selectedKB
    : null;

  if (displayedKB) {
    return (
      <KBDocumentPage
        kb={displayedKB}
        onBack={() => setSelectedKB(null)}
        folderName={folder?.Name as string}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/ai/knowledge-base" className="text-foreground hover:text-foreground">
                {BREADCRUMB_CUSTOM_TITLES['/ai/knowledge-base'] || t('KNOWLEDGE_BASE')}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage className="text-low-emphasis">{folder?.Name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-col gap-2">
        {/* Desktop Header */}
        <div className="hidden flex-row items-center justify-between md:flex">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 className="tracking-tigh flex items-center gap-2 text-2xl font-bold">
              {folder?.Name}
            </h3>
            <p className="break-all text-sm text-muted-foreground">{folder?.Description}</p>
          </div>
          <div className="flex flex-row">
            <Button
              variant="outline"
              className="ml-2 flex flex-row"
              onClick={() => setIsConfigureModalOpen(true)}
            >
              <Settings className="mr-2 h-4 w-4" />
              {t('CONFIGURE')}
            </Button>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="flex flex-col gap-2 md:hidden">
          <div className="flex flex-row items-center justify-between gap-2">
            <h3 className="truncate tracking-tight text-2xl font-bold">{folder?.Name}</h3>
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0"
              onClick={() => setIsConfigureModalOpen(true)}
            >
              <Settings className="mr-2 h-4 w-4" />
              {t('CONFIGURE')}
            </Button>
          </div>
          <p className="break-all text-sm text-muted-foreground">{folder?.Description}</p>
        </div>

        <div className="flex flex-row gap-2 bg-border w-fit rounded-md px-2">
          <CircleSpark className="aspect-square w-4 p-0 pb-1 text-cyan-700" />
          <span className="p-0 text-sm text-medium-emphasis">{t('ASSOCIATED_AGENTS')}</span>
          <span className="p-0 text-sm font-medium text-high-emphasis">
            {associatedAgentCount?.data?.total_count || 0}
          </span>
        </div>

        <div className="mt-4 flex w-full flex-1 gap-6">
          <Tabs
            orientation="vertical"
            value={activeTab || KBType.FILE}
            onValueChange={setActiveTab}
            className="flex w-full flex-col gap-6 md:flex-row"
          >
            {/* Mobile Select */}
            <div className="w-full md:hidden">
              <Select value={activeTab || KBType.FILE} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('SELECT_TYPE')} />
                </SelectTrigger>
                <SelectContent>
                  {KB_TABS.map(({ value, label, icon: Icon }) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {t(label)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <TabsList className="hidden h-fit w-[250px] flex-col justify-start gap-2 bg-transparent p-0 md:flex">
              {KB_TABS.map(({ value, label, icon: Icon }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="w-full justify-start gap-2 border bg-card px-4 py-3 font-normal text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1">
              <Card className="w-full">
                <CardContent>
                  <KBDocumentsTable
                    knowledgeBases={knowledgeBases}
                    isLoading={isLoading}
                    type={activeTab as KBType}
                    folderId={folderId}
                    totalCount={totalCount}
                    page={filter.offset}
                    pageSize={filter.limit}
                    onPageChange={(page) => setFilter((prev) => ({ ...prev, offset: page }))}
                    onPageSizeChange={(pageSize) =>
                      setFilter((prev) => ({ ...prev, limit: pageSize, offset: 0 }))
                    }
                    onRowClick={setSelectedKB}
                  />
                </CardContent>
              </Card>
            </div>
          </Tabs>
        </div>
        <FolderConfigureModal
          open={isConfigureModalOpen}
          onOpenChange={setIsConfigureModalOpen}
          folder={folder as IFolder}
        />
      </div>
    </div>
  );
};
